import type { Coverage, CoverageEligibilityResponse } from '@medplum/fhirtypes';
import type { VerificationEntry, VerificationStatus } from '@/components/billing/types';
import { cerToVerificationEntry } from '@/components/billing/helpers';

export type VerificationHistoryStatus =
  | 'verified'
  | 'inactive'
  | 'pending'
  | 'failed'
  | 'needs_review'
  | 'unknown';

export interface VerificationHistoryEntry extends VerificationEntry {
  historyStatus: VerificationHistoryStatus;
  payerMessage?: string;
  visitLimits?: string;
  verifiedBy?: string;
}

function mapCerOutcome(outcome?: string): VerificationHistoryStatus {
  if (outcome === 'complete') return 'verified';
  if (outcome === 'queued') return 'pending';
  if (outcome === 'error') return 'failed';
  return 'unknown';
}

export function cerToVerificationHistoryEntry(
  cer: CoverageEligibilityResponse,
  coverages: Coverage[],
): VerificationHistoryEntry | null {
  const base = cerToVerificationEntry(cer, coverages);
  if (!base) return null;
  const inforce = base.inforce;
  const historyStatus: VerificationHistoryStatus =
    cer.outcome === 'complete'
      ? inforce === false
        ? 'inactive'
        : 'verified'
      : mapCerOutcome(cer.outcome);
  const matched = cer.insurance?.find((ins) => ins.coverage?.reference?.split('/')[1] === base.insuranceId)
    ?? cer.insurance?.[0];
  const visitLimits = matched?.item?.[0]?.authorizationRequired
    ? 'Authorization may be required'
    : undefined;
  return {
    ...base,
    status: base.status as VerificationStatus,
    historyStatus,
    payerMessage: cer.disposition,
    visitLimits,
    verifiedBy: cer.requestor?.display ?? cer.requestor?.reference,
    verifiedAt: cer.created,
  };
}

export function sortVerificationHistory(
  entries: VerificationHistoryEntry[],
): VerificationHistoryEntry[] {
  return [...entries].sort((a, b) => {
    const da = new Date(a.verifiedAt ?? 0).getTime();
    const db = new Date(b.verifiedAt ?? 0).getTime();
    return db - da;
  });
}

export function verificationHistoryStatusLabel(status: VerificationHistoryStatus): string {
  const labels: Record<VerificationHistoryStatus, string> = {
    verified: 'Verified',
    inactive: 'Inactive',
    pending: 'Pending',
    failed: 'Failed',
    needs_review: 'Needs review',
    unknown: 'Unknown',
  };
  return labels[status] ?? 'Unknown';
}

export function verificationHistoryStatusColor(status: VerificationHistoryStatus): string {
  switch (status) {
    case 'verified': return 'green';
    case 'inactive': return 'gray';
    case 'pending': return 'blue';
    case 'failed': return 'red';
    case 'needs_review': return 'yellow';
    default: return 'gray';
  }
}

export function clientPortalVerificationLabel(status: VerificationHistoryStatus): string {
  switch (status) {
    case 'verified': return 'Verified';
    case 'pending': return 'Pending review';
    case 'failed':
    case 'needs_review':
    case 'inactive':
      return 'Needs updated information';
    default: return 'Not verified yet';
  }
}
